// src/errors/XoneCryptoError.ts
var XoneCryptoError = class extends Error {
  name = "XoneCryptoError";
  code;
  details;
  constructor(code, message, details) {
    super(message);
    this.code = code;
    if (details !== void 0) {
      this.details = details;
    }
  }
};

// src/errors/codes.ts
var XONE_CRYPTO_ERROR_CODES = {
  /** Algoritmo no soportado por `SubtleCrypto` o por la libreria. */
  UNSUPPORTED_ALGORITHM: "XONE.CRYPTO.UNSUPPORTED_ALGORITHM",
  /** Argumento invalido (longitud, formato, tipo). */
  INVALID_INPUT: "XONE.CRYPTO.INVALID_INPUT",
  /** Codificacion solicitada fuera del catalogo permitido. */
  INVALID_ENCODING: "XONE.CRYPTO.INVALID_ENCODING",
  /** PEM malformado o cuerpo base64 invalido. */
  INVALID_PEM: "XONE.CRYPTO.INVALID_PEM",
  /** Ciphertext AES-GCM truncado o con tag invalido. */
  INVALID_CIPHERTEXT: "XONE.CRYPTO.INVALID_CIPHERTEXT",
  /** Operacion fallida en `SubtleCrypto` (autenticidad, key import, etc.). */
  OPERATION_FAILED: "XONE.CRYPTO.OPERATION_FAILED",
  /** Clave/cert/privada destruida — ya no se puede usar. */
  KEY_DESTROYED: "XONE.CRYPTO.KEY_DESTROYED",
  /** Certificado fuera de su periodo de validez. */
  CERT_NOT_VALID: "XONE.CRYPTO.CERT_NOT_VALID",
  /** Writer no configurado para `toPemFile`. */
  NO_FILE_WRITER: "XONE.CRYPTO.NO_FILE_WRITER",
  /** `SubtleCrypto` no disponible en el entorno actual. */
  SUBTLE_UNAVAILABLE: "XONE.CRYPTO.SUBTLE_UNAVAILABLE"
};

// src/encoding/base64.ts
var B64_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
var B64_LOOKUP = (() => {
  const arr = new Int16Array(128);
  arr.fill(-1);
  for (let i = 0; i < B64_CHARS.length; i++) {
    arr[B64_CHARS.charCodeAt(i)] = i;
  }
  return arr;
})();
function bytesToBase64(bytes) {
  let output = "";
  let i = 0;
  const len = bytes.length;
  for (; i + 2 < len; i += 3) {
    const b1 = bytes[i];
    const b2 = bytes[i + 1];
    const b3 = bytes[i + 2];
    output += B64_CHARS[b1 >> 2];
    output += B64_CHARS[(b1 & 3) << 4 | b2 >> 4];
    output += B64_CHARS[(b2 & 15) << 2 | b3 >> 6];
    output += B64_CHARS[b3 & 63];
  }
  const rem = len - i;
  if (rem === 1) {
    const b1 = bytes[i];
    output += B64_CHARS[b1 >> 2];
    output += B64_CHARS[(b1 & 3) << 4];
    output += "==";
  } else if (rem === 2) {
    const b1 = bytes[i];
    const b2 = bytes[i + 1];
    output += B64_CHARS[b1 >> 2];
    output += B64_CHARS[(b1 & 3) << 4 | b2 >> 4];
    output += B64_CHARS[(b2 & 15) << 2];
    output += "=";
  }
  return output;
}
function base64ToBytes(b64) {
  if (typeof b64 !== "string") {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.INVALID_INPUT,
      "base64ToBytes: la entrada debe ser string"
    );
  }
  const clean = b64.replace(/\s+/g, "");
  if (clean.length === 0) {
    return new Uint8Array(0);
  }
  if (clean.length % 4 !== 0) {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.INVALID_INPUT,
      "base64ToBytes: longitud invalida (no multiplo de 4)",
      { length: clean.length }
    );
  }
  let padding = 0;
  if (clean.endsWith("==")) padding = 2;
  else if (clean.endsWith("=")) padding = 1;
  const outLen = clean.length / 4 * 3 - padding;
  const out = new Uint8Array(outLen);
  let oi = 0;
  for (let i = 0; i < clean.length; i += 4) {
    const c1 = clean.charCodeAt(i);
    const c2 = clean.charCodeAt(i + 1);
    const c3 = clean.charCodeAt(i + 2);
    const c4 = clean.charCodeAt(i + 3);
    const v1 = c1 < 128 ? B64_LOOKUP[c1] ?? -1 : -1;
    const v2 = c2 < 128 ? B64_LOOKUP[c2] ?? -1 : -1;
    const v3 = c3 === 61 ? 0 : c3 < 128 ? B64_LOOKUP[c3] ?? -1 : -1;
    const v4 = c4 === 61 ? 0 : c4 < 128 ? B64_LOOKUP[c4] ?? -1 : -1;
    if (v1 < 0 || v2 < 0 || v3 < 0 || v4 < 0) {
      throw new XoneCryptoError(
        XONE_CRYPTO_ERROR_CODES.INVALID_INPUT,
        "base64ToBytes: caracter no valido",
        { index: i }
      );
    }
    const b1 = v1 << 2 | v2 >> 4;
    const b2 = (v2 & 15) << 4 | v3 >> 2;
    const b3 = (v3 & 3) << 6 | v4;
    if (oi < outLen) out[oi++] = b1;
    if (oi < outLen) out[oi++] = b2;
    if (oi < outLen) out[oi++] = b3;
  }
  return out;
}

// src/encoding/hex.ts
var HEX_CHARS = "0123456789abcdef";
function bytesToHex(bytes) {
  let out = "";
  for (let i = 0; i < bytes.length; i++) {
    const v = bytes[i];
    out += HEX_CHARS[v >> 4];
    out += HEX_CHARS[v & 15];
  }
  return out;
}
function hexToBytes(hex) {
  if (typeof hex !== "string") {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.INVALID_INPUT,
      "hexToBytes: la entrada debe ser string"
    );
  }
  const clean = hex.replace(/\s+/g, "");
  if (clean.length % 2 !== 0) {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.INVALID_INPUT,
      "hexToBytes: longitud impar",
      { length: clean.length }
    );
  }
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < clean.length; i += 2) {
    const hi = digit(clean.charCodeAt(i));
    const lo = digit(clean.charCodeAt(i + 1));
    if (hi < 0 || lo < 0) {
      throw new XoneCryptoError(
        XONE_CRYPTO_ERROR_CODES.INVALID_INPUT,
        "hexToBytes: caracter no hexadecimal",
        { index: i }
      );
    }
    out[i / 2] = hi << 4 | lo;
  }
  return out;
}
function digit(code) {
  if (code >= 48 && code <= 57) return code - 48;
  if (code >= 97 && code <= 102) return code - 97 + 10;
  if (code >= 65 && code <= 70) return code - 65 + 10;
  return -1;
}

// src/primitives/subtle.ts
function getSubtle() {
  const c = globalThis.crypto;
  if (!c || !c.subtle) {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.SUBTLE_UNAVAILABLE,
      "SubtleCrypto no disponible en el entorno actual"
    );
  }
  return c.subtle;
}
var TEXT_ENCODER = new TextEncoder();
var TEXT_DECODER = new TextDecoder("utf-8", { fatal: true });
function toBytes(input) {
  if (typeof input === "string") {
    return TEXT_ENCODER.encode(input);
  }
  if (input instanceof Uint8Array) {
    return input;
  }
  throw new XoneCryptoError(
    XONE_CRYPTO_ERROR_CODES.INVALID_INPUT,
    "toBytes: la entrada debe ser string o Uint8Array",
    { typeof: typeof input }
  );
}
function bytesToUtf8(bytes) {
  try {
    return TEXT_DECODER.decode(bytes);
  } catch (cause) {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.OPERATION_FAILED,
      "bytesToUtf8: secuencia UTF-8 invalida tras descifrar",
      { cause: cause.message }
    );
  }
}
function asBytes(buffer) {
  if (buffer instanceof Uint8Array) {
    return buffer;
  }
  if (ArrayBuffer.isView(buffer)) {
    return new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength);
  }
  return new Uint8Array(buffer);
}

// src/primitives/hash.ts
var HASH_ALGORITHMS = {
  sha1: "SHA-1",
  "sha-1": "SHA-1",
  sha256: "SHA-256",
  "sha-256": "SHA-256",
  sha384: "SHA-384",
  "sha-384": "SHA-384",
  sha512: "SHA-512",
  "sha-512": "SHA-512"
};
function normalizeAlgorithm(name) {
  const key = name.toLowerCase();
  const norm = HASH_ALGORITHMS[key];
  if (!norm) {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.UNSUPPORTED_ALGORITHM,
      `hash: algoritmo no soportado: ${name}`,
      { algorithm: name }
    );
  }
  return norm;
}
function encode(bytes, encoding) {
  if (encoding === "hex") return bytesToHex(bytes);
  if (encoding === "base64") return bytesToBase64(bytes);
  throw new XoneCryptoError(
    XONE_CRYPTO_ERROR_CODES.INVALID_ENCODING,
    `Codificacion no soportada: ${String(encoding)}`
  );
}
async function hash(algorithm, input, encoding = "hex") {
  const algo = normalizeAlgorithm(algorithm);
  const data = toBytes(input);
  try {
    const buf = await getSubtle().digest(algo, data);
    return encode(asBytes(buf), encoding);
  } catch (cause) {
    if (cause instanceof XoneCryptoError) throw cause;
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.OPERATION_FAILED,
      `hash: SubtleCrypto.digest fallo (${algo})`,
      { algorithm: algo, cause: cause.message }
    );
  }
}
function isSupportedHashAlgorithm(name) {
  return Object.prototype.hasOwnProperty.call(HASH_ALGORITHMS, name.toLowerCase());
}

// src/primitives/hmac.ts
var HMAC_ALGORITHMS = {
  "hmac-sha1": "SHA-1",
  "hmac-sha256": "SHA-256",
  "hmac-sha384": "SHA-384",
  "hmac-sha512": "SHA-512",
  "hmacsha1": "SHA-1",
  "hmacsha256": "SHA-256",
  "hmacsha384": "SHA-384",
  "hmacsha512": "SHA-512",
  // Aliases sueltos (sin prefijo) — practicos.
  "sha1": "SHA-1",
  "sha256": "SHA-256",
  "sha384": "SHA-384",
  "sha512": "SHA-512",
  "sha-1": "SHA-1",
  "sha-256": "SHA-256",
  "sha-384": "SHA-384",
  "sha-512": "SHA-512"
};
function normalizeHmac(name) {
  const key = name.toLowerCase();
  const norm = HMAC_ALGORITHMS[key];
  if (!norm) {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.UNSUPPORTED_ALGORITHM,
      `hmac: algoritmo no soportado: ${name}`,
      { algorithm: name }
    );
  }
  return norm;
}
function encode2(bytes, encoding) {
  if (encoding === "hex") return bytesToHex(bytes);
  if (encoding === "base64") return bytesToBase64(bytes);
  throw new XoneCryptoError(
    XONE_CRYPTO_ERROR_CODES.INVALID_ENCODING,
    `Codificacion no soportada: ${String(encoding)}`
  );
}
async function hmac(algorithm, key, input, encoding = "hex") {
  const hashName = normalizeHmac(algorithm);
  const subtle = getSubtle();
  const keyBytes = toBytes(key);
  const dataBytes = toBytes(input);
  if (keyBytes.length === 0) {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.INVALID_INPUT,
      "hmac: la clave no puede estar vacia"
    );
  }
  try {
    const cryptoKey = await subtle.importKey(
      "raw",
      keyBytes,
      { name: "HMAC", hash: hashName },
      false,
      ["sign"]
    );
    const sig = await subtle.sign("HMAC", cryptoKey, dataBytes);
    return encode2(asBytes(sig), encoding);
  } catch (cause) {
    if (cause instanceof XoneCryptoError) throw cause;
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.OPERATION_FAILED,
      `hmac: SubtleCrypto fallo (${hashName})`,
      { algorithm: hashName, cause: cause.message }
    );
  }
}

// src/primitives/aes.ts
var IV_LENGTH = 12;
var TAG_LENGTH_BITS = 128;
async function deriveAesKey(material) {
  const subtle = getSubtle();
  const raw = material.length === 32 ? material : asBytes(await subtle.digest("SHA-256", material));
  return subtle.importKey(
    "raw",
    raw,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}
async function encrypt(plain, key) {
  const subtle = getSubtle();
  const plainBytes = toBytes(plain);
  const keyBytes = toBytes(key);
  if (keyBytes.length === 0) {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.INVALID_INPUT,
      "encrypt: clave vacia"
    );
  }
  const cryptoKey = await deriveAesKey(keyBytes);
  const iv = new Uint8Array(IV_LENGTH);
  globalThis.crypto.getRandomValues(iv);
  let cipherBuf;
  try {
    cipherBuf = await subtle.encrypt(
      { name: "AES-GCM", iv, tagLength: TAG_LENGTH_BITS },
      cryptoKey,
      plainBytes
    );
  } catch (cause) {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.OPERATION_FAILED,
      "encrypt: SubtleCrypto.encrypt fallo",
      { cause: cause.message }
    );
  }
  const cipherBytes = asBytes(cipherBuf);
  const out = new Uint8Array(IV_LENGTH + cipherBytes.length);
  out.set(iv, 0);
  out.set(cipherBytes, IV_LENGTH);
  return bytesToBase64(out);
}
async function decrypt(cipher, key) {
  if (typeof cipher !== "string") {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.INVALID_INPUT,
      "decrypt: cipher debe ser string base64"
    );
  }
  const subtle = getSubtle();
  const raw = base64ToBytes(cipher);
  if (raw.length <= IV_LENGTH + TAG_LENGTH_BITS / 8) {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.INVALID_CIPHERTEXT,
      "decrypt: ciphertext demasiado corto",
      { length: raw.length }
    );
  }
  const iv = raw.subarray(0, IV_LENGTH);
  const body = raw.subarray(IV_LENGTH);
  const keyBytes = toBytes(key);
  const cryptoKey = await deriveAesKey(keyBytes);
  let plainBuf;
  try {
    plainBuf = await subtle.decrypt(
      { name: "AES-GCM", iv, tagLength: TAG_LENGTH_BITS },
      cryptoKey,
      body
    );
  } catch (cause) {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.OPERATION_FAILED,
      "decrypt: autenticidad o clave invalida",
      { cause: cause.message }
    );
  }
  return bytesToUtf8(asBytes(plainBuf));
}

// src/encoding/pem.ts
var PEM_REGEX = /-----BEGIN ([A-Z0-9 ]+)-----\s*([A-Za-z0-9+/=\s]+?)\s*-----END \1-----/;
function parsePem(pem, expectedLabel) {
  if (typeof pem !== "string") {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.INVALID_PEM,
      "parsePem: la entrada debe ser string"
    );
  }
  const match = PEM_REGEX.exec(pem);
  if (!match) {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.INVALID_PEM,
      "parsePem: no se encontro un bloque PEM valido"
    );
  }
  const label = match[1].trim().toUpperCase();
  const bodyText = match[2];
  if (expectedLabel !== void 0 && label !== expectedLabel.toUpperCase()) {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.INVALID_PEM,
      `parsePem: etiqueta inesperada (${label}), se esperaba ${expectedLabel}`,
      { actual: label, expected: expectedLabel }
    );
  }
  let body;
  try {
    body = base64ToBytes(bodyText);
  } catch (cause) {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.INVALID_PEM,
      "parsePem: cuerpo base64 invalido",
      { cause: cause.message }
    );
  }
  return { label, body };
}
function bytesToPem(label, bytes) {
  const upper = label.trim().toUpperCase();
  const b64 = bytesToBase64(bytes);
  const lines = [];
  for (let i = 0; i < b64.length; i += 64) {
    lines.push(b64.slice(i, i + 64));
  }
  return `-----BEGIN ${upper}-----
${lines.join("\n")}
-----END ${upper}-----
`;
}

// src/primitives/rsa.ts
var HASH_NAMES = {
  sha256: "SHA-256",
  sha384: "SHA-384",
  sha512: "SHA-512"
};
function isRsaAlgorithm(name) {
  return name === "sha256" || name === "sha384" || name === "sha512";
}
async function importRsaPublicKey(pem, hashName) {
  const block = parsePem(pem);
  if (block.label !== "PUBLIC KEY" && block.label !== "RSA PUBLIC KEY") {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.INVALID_PEM,
      `importRsaPublicKey: etiqueta PEM no soportada: ${block.label}`,
      { label: block.label }
    );
  }
  try {
    return await getSubtle().importKey(
      "spki",
      block.body,
      { name: "RSASSA-PKCS1-v1_5", hash: hashName },
      false,
      ["verify"]
    );
  } catch (cause) {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.OPERATION_FAILED,
      "importRsaPublicKey: SubtleCrypto.importKey fallo",
      { cause: cause.message }
    );
  }
}
async function verify(algorithm, publicKeyPem, data, signature) {
  const algoLc = String(algorithm).toLowerCase();
  if (!isRsaAlgorithm(algoLc)) {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.UNSUPPORTED_ALGORITHM,
      `verify: algoritmo no soportado: ${algorithm}`,
      { algorithm }
    );
  }
  if (!(signature instanceof Uint8Array)) {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.INVALID_INPUT,
      "verify: la firma debe ser Uint8Array"
    );
  }
  const hashName = HASH_NAMES[algoLc];
  const key = await importRsaPublicKey(publicKeyPem, hashName);
  const dataBytes = toBytes(data);
  try {
    return await getSubtle().verify(
      "RSASSA-PKCS1-v1_5",
      key,
      signature,
      dataBytes
    );
  } catch (cause) {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.OPERATION_FAILED,
      "verify: SubtleCrypto.verify fallo",
      { cause: cause.message }
    );
  }
}

// src/primitives/randomBytes.ts
function randomBytes(length) {
  if (!Number.isInteger(length) || length < 0) {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.INVALID_INPUT,
      "randomBytes: length debe ser un entero >= 0",
      { length }
    );
  }
  if (length > 65536) {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.INVALID_INPUT,
      "randomBytes: length excede el maximo permitido (65536)",
      { length }
    );
  }
  const c = globalThis.crypto;
  if (!c || typeof c.getRandomValues !== "function") {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.SUBTLE_UNAVAILABLE,
      "randomBytes: crypto.getRandomValues no disponible"
    );
  }
  const out = new Uint8Array(length);
  c.getRandomValues(out);
  return out;
}

// src/primitives/md5.ts
function add32(a, b) {
  return a + b & 4294967295;
}
function rotl(x, n) {
  return (x << n | x >>> 32 - n) & 4294967295;
}
function md5cycle(x, k) {
  let a = x[0];
  let b = x[1];
  let c = x[2];
  let d = x[3];
  const ff = (a2, b2, c2, d2, x2, s, t) => add32(rotl(add32(add32(a2, b2 & c2 | ~b2 & d2), add32(x2, t)), s), b2);
  const gg = (a2, b2, c2, d2, x2, s, t) => add32(rotl(add32(add32(a2, b2 & d2 | c2 & ~d2), add32(x2, t)), s), b2);
  const hh = (a2, b2, c2, d2, x2, s, t) => add32(rotl(add32(add32(a2, b2 ^ c2 ^ d2), add32(x2, t)), s), b2);
  const ii = (a2, b2, c2, d2, x2, s, t) => add32(rotl(add32(add32(a2, c2 ^ (b2 | ~d2)), add32(x2, t)), s), b2);
  a = ff(a, b, c, d, k[0], 7, -680876936);
  d = ff(d, a, b, c, k[1], 12, -389564586);
  c = ff(c, d, a, b, k[2], 17, 606105819);
  b = ff(b, c, d, a, k[3], 22, -1044525330);
  a = ff(a, b, c, d, k[4], 7, -176418897);
  d = ff(d, a, b, c, k[5], 12, 1200080426);
  c = ff(c, d, a, b, k[6], 17, -1473231341);
  b = ff(b, c, d, a, k[7], 22, -45705983);
  a = ff(a, b, c, d, k[8], 7, 1770035416);
  d = ff(d, a, b, c, k[9], 12, -1958414417);
  c = ff(c, d, a, b, k[10], 17, -42063);
  b = ff(b, c, d, a, k[11], 22, -1990404162);
  a = ff(a, b, c, d, k[12], 7, 1804603682);
  d = ff(d, a, b, c, k[13], 12, -40341101);
  c = ff(c, d, a, b, k[14], 17, -1502002290);
  b = ff(b, c, d, a, k[15], 22, 1236535329);
  a = gg(a, b, c, d, k[1], 5, -165796510);
  d = gg(d, a, b, c, k[6], 9, -1069501632);
  c = gg(c, d, a, b, k[11], 14, 643717713);
  b = gg(b, c, d, a, k[0], 20, -373897302);
  a = gg(a, b, c, d, k[5], 5, -701558691);
  d = gg(d, a, b, c, k[10], 9, 38016083);
  c = gg(c, d, a, b, k[15], 14, -660478335);
  b = gg(b, c, d, a, k[4], 20, -405537848);
  a = gg(a, b, c, d, k[9], 5, 568446438);
  d = gg(d, a, b, c, k[14], 9, -1019803690);
  c = gg(c, d, a, b, k[3], 14, -187363961);
  b = gg(b, c, d, a, k[8], 20, 1163531501);
  a = gg(a, b, c, d, k[13], 5, -1444681467);
  d = gg(d, a, b, c, k[2], 9, -51403784);
  c = gg(c, d, a, b, k[7], 14, 1735328473);
  b = gg(b, c, d, a, k[12], 20, -1926607734);
  a = hh(a, b, c, d, k[5], 4, -378558);
  d = hh(d, a, b, c, k[8], 11, -2022574463);
  c = hh(c, d, a, b, k[11], 16, 1839030562);
  b = hh(b, c, d, a, k[14], 23, -35309556);
  a = hh(a, b, c, d, k[1], 4, -1530992060);
  d = hh(d, a, b, c, k[4], 11, 1272893353);
  c = hh(c, d, a, b, k[7], 16, -155497632);
  b = hh(b, c, d, a, k[10], 23, -1094730640);
  a = hh(a, b, c, d, k[13], 4, 681279174);
  d = hh(d, a, b, c, k[0], 11, -358537222);
  c = hh(c, d, a, b, k[3], 16, -722521979);
  b = hh(b, c, d, a, k[6], 23, 76029189);
  a = hh(a, b, c, d, k[9], 4, -640364487);
  d = hh(d, a, b, c, k[12], 11, -421815835);
  c = hh(c, d, a, b, k[15], 16, 530742520);
  b = hh(b, c, d, a, k[2], 23, -995338651);
  a = ii(a, b, c, d, k[0], 6, -198630844);
  d = ii(d, a, b, c, k[7], 10, 1126891415);
  c = ii(c, d, a, b, k[14], 15, -1416354905);
  b = ii(b, c, d, a, k[5], 21, -57434055);
  a = ii(a, b, c, d, k[12], 6, 1700485571);
  d = ii(d, a, b, c, k[3], 10, -1894986606);
  c = ii(c, d, a, b, k[10], 15, -1051523);
  b = ii(b, c, d, a, k[1], 21, -2054922799);
  a = ii(a, b, c, d, k[8], 6, 1873313359);
  d = ii(d, a, b, c, k[15], 10, -30611744);
  c = ii(c, d, a, b, k[6], 15, -1560198380);
  b = ii(b, c, d, a, k[13], 21, 1309151649);
  a = ii(a, b, c, d, k[4], 6, -145523070);
  d = ii(d, a, b, c, k[11], 10, -1120210379);
  c = ii(c, d, a, b, k[2], 15, 718787259);
  b = ii(b, c, d, a, k[9], 21, -343485551);
  x[0] = add32(x[0], a);
  x[1] = add32(x[1], b);
  x[2] = add32(x[2], c);
  x[3] = add32(x[3], d);
}
function md5blk(s, off) {
  const md5blks = [];
  for (let i = 0; i < 64; i += 4) {
    md5blks.push(
      s[off + i] + (s[off + i + 1] << 8) + (s[off + i + 2] << 16) + (s[off + i + 3] << 24)
    );
  }
  return md5blks;
}
function md5digest(bytes) {
  const len = bytes.length;
  const state = [1732584193, -271733879, -1732584194, 271733878];
  let i;
  for (i = 0; i + 64 <= len; i += 64) {
    md5cycle(state, md5blk(bytes, i));
  }
  const tail = new Array(16).fill(0);
  let j = 0;
  for (; i < len; i++) {
    tail[j >> 2] = tail[j >> 2] | bytes[i] << (j % 4 << 3);
    j++;
  }
  tail[j >> 2] = tail[j >> 2] | 128 << (j % 4 << 3);
  if (j > 55) {
    md5cycle(state, tail);
    for (let k = 0; k < 16; k++) tail[k] = 0;
  }
  const bits = len * 8;
  tail[14] = bits >>> 0;
  tail[15] = Math.floor(bits / 4294967296);
  md5cycle(state, tail);
  const out = new Uint8Array(16);
  for (let k = 0; k < 4; k++) {
    const v = state[k];
    out[k * 4] = v & 255;
    out[k * 4 + 1] = v >>> 8 & 255;
    out[k * 4 + 2] = v >>> 16 & 255;
    out[k * 4 + 3] = v >>> 24 & 255;
  }
  return out;
}
function encodeOut(bytes, encoding) {
  if (encoding === "hex") return bytesToHex(bytes);
  if (encoding === "base64") return bytesToBase64(bytes);
  throw new XoneCryptoError(
    XONE_CRYPTO_ERROR_CODES.INVALID_ENCODING,
    `Codificacion no soportada: ${String(encoding)}`
  );
}
async function md5(input, encoding = "hex") {
  const data = toBytes(input);
  const digest = md5digest(data);
  return encodeOut(digest, encoding);
}

// src/primitives/randomUtils.ts
var ALPHANUM = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
function getNewUuid() {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") {
    return c.randomUUID();
  }
  const b = randomBytes(16);
  b[6] = b[6] & 15 | 64;
  b[8] = b[8] & 63 | 128;
  const hex = [];
  for (let i = 0; i < 16; i++) {
    hex.push((b[i] + 256).toString(16).slice(1));
  }
  return hex.slice(0, 4).join("") + "-" + hex.slice(4, 6).join("") + "-" + hex.slice(6, 8).join("") + "-" + hex.slice(8, 10).join("") + "-" + hex.slice(10, 16).join("");
}
function getRandomString(length) {
  if (!Number.isInteger(length) || length < 0) {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.INVALID_INPUT,
      "getRandomString: length debe ser un entero >= 0",
      { length }
    );
  }
  if (length === 0) return "";
  const max = 248;
  const out = [];
  while (out.length < length) {
    const buf = randomBytes(Math.min(length * 2, 256));
    for (let i = 0; i < buf.length && out.length < length; i++) {
      const b = buf[i];
      if (b < max) out.push(ALPHANUM[b % 62]);
    }
  }
  return out.join("");
}
function getRandomInt() {
  const b = randomBytes(4);
  return new Int32Array(
    b.buffer.slice(b.byteOffset, b.byteOffset + 4)
  )[0];
}
function getRandomDouble() {
  const b = randomBytes(8);
  const view = new DataView(b.buffer, b.byteOffset, 8);
  const hi = view.getUint32(0, true) >>> 5;
  const lo = view.getUint32(4, true) >>> 6;
  return (hi * 67108864 + lo) / 9007199254740992;
}

// src/primitives/compression.ts
function pickFormat(opts) {
  if (opts?.gzip === true) return "gzip";
  return "deflate";
}
async function streamThrough(data, transform) {
  const writer = transform.writable.getWriter();
  void writer.write(data);
  void writer.close();
  const reader = transform.readable.getReader();
  const chunks = [];
  let total = 0;
  for (; ; ) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) {
      chunks.push(value);
      total += value.length;
    }
  }
  const out = new Uint8Array(total);
  let off = 0;
  for (const c of chunks) {
    out.set(c, off);
    off += c.length;
  }
  return out;
}
async function deflate(input, options) {
  const Ctor = globalThis.CompressionStream;
  if (!Ctor) {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.OPERATION_FAILED,
      "deflate: CompressionStream no disponible en el runtime actual"
    );
  }
  const data = toBytes(input);
  const format = pickFormat(options);
  try {
    return await streamThrough(data, new Ctor(format));
  } catch (cause) {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.OPERATION_FAILED,
      `deflate: CompressionStream(${format}) fallo`,
      { format, cause: cause.message }
    );
  }
}
async function inflate(input, options) {
  const Ctor = globalThis.DecompressionStream;
  if (!Ctor) {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.OPERATION_FAILED,
      "inflate: DecompressionStream no disponible en el runtime actual"
    );
  }
  if (!(input instanceof Uint8Array)) {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.INVALID_INPUT,
      "inflate: input debe ser Uint8Array"
    );
  }
  const format = pickFormat(options);
  try {
    return await streamThrough(input, new Ctor(format));
  } catch (cause) {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.OPERATION_FAILED,
      `inflate: DecompressionStream(${format}) fallo`,
      { format, cause: cause.message }
    );
  }
}

// src/encoding/base32.ts
var B32_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";
var B32_LOOKUP = (() => {
  const arr = new Int16Array(128);
  arr.fill(-1);
  for (let i = 0; i < B32_CHARS.length; i++) arr[B32_CHARS.charCodeAt(i)] = i;
  return arr;
})();
function bytesToBase32(bytes) {
  let out = "";
  let bits = 0;
  let value = 0;
  for (let i = 0; i < bytes.length; i++) {
    value = value << 8 | bytes[i];
    bits += 8;
    while (bits >= 5) {
      out += B32_CHARS[value >>> bits - 5 & 31];
      bits -= 5;
    }
  }
  if (bits > 0) {
    out += B32_CHARS[value << 5 - bits & 31];
  }
  while (out.length % 8 !== 0) out += "=";
  return out;
}
function base32ToBytes(text) {
  if (typeof text !== "string") {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.INVALID_INPUT,
      "base32ToBytes: la entrada debe ser string"
    );
  }
  const clean = text.replace(/=+$/, "").toUpperCase();
  const out = [];
  let bits = 0;
  let value = 0;
  for (let i = 0; i < clean.length; i++) {
    const ch = clean.charCodeAt(i);
    const v = ch < 128 ? B32_LOOKUP[ch] ?? -1 : -1;
    if (v < 0) {
      throw new XoneCryptoError(
        XONE_CRYPTO_ERROR_CODES.INVALID_INPUT,
        "base32ToBytes: caracter no valido",
        { index: i, char: clean[i] }
      );
    }
    value = value << 5 | v;
    bits += 5;
    if (bits >= 8) {
      out.push(value >>> bits - 8 & 255);
      bits -= 8;
    }
  }
  return new Uint8Array(out);
}

// src/encoding/base45.ts
var B45_CHARS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ $%*+-./:";
var B45_LOOKUP = (() => {
  const arr = new Int16Array(128);
  arr.fill(-1);
  for (let i = 0; i < B45_CHARS.length; i++) arr[B45_CHARS.charCodeAt(i)] = i;
  return arr;
})();
function bytesToBase45(bytes) {
  let out = "";
  let i = 0;
  for (; i + 2 <= bytes.length; i += 2) {
    const x = bytes[i] << 8 | bytes[i + 1];
    const e = Math.floor(x / (45 * 45));
    const d = Math.floor(x % (45 * 45) / 45);
    const c = x % 45;
    out += B45_CHARS[c];
    out += B45_CHARS[d];
    out += B45_CHARS[e];
  }
  if (i < bytes.length) {
    const x = bytes[i];
    const d = Math.floor(x / 45);
    const c = x % 45;
    out += B45_CHARS[c];
    out += B45_CHARS[d];
  }
  return out;
}
function base45ToBytes(text) {
  if (typeof text !== "string") {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.INVALID_INPUT,
      "base45ToBytes: la entrada debe ser string"
    );
  }
  const out = [];
  let i = 0;
  while (i < text.length) {
    const remaining = text.length - i;
    if (remaining >= 3) {
      const c = decodeChar(text, i);
      const d = decodeChar(text, i + 1);
      const e = decodeChar(text, i + 2);
      const x = c + d * 45 + e * 45 * 45;
      if (x > 65535) {
        throw new XoneCryptoError(
          XONE_CRYPTO_ERROR_CODES.INVALID_INPUT,
          "base45ToBytes: triplet fuera de rango",
          { index: i }
        );
      }
      out.push(x >> 8 & 255);
      out.push(x & 255);
      i += 3;
    } else if (remaining === 2) {
      const c = decodeChar(text, i);
      const d = decodeChar(text, i + 1);
      const x = c + d * 45;
      out.push(x & 255);
      i += 2;
    } else {
      throw new XoneCryptoError(
        XONE_CRYPTO_ERROR_CODES.INVALID_INPUT,
        "base45ToBytes: longitud invalida (resto 1 caracter)"
      );
    }
  }
  return new Uint8Array(out);
}
function decodeChar(text, idx) {
  const ch = text.charCodeAt(idx);
  const v = ch < 128 ? B45_LOOKUP[ch] ?? -1 : -1;
  if (v < 0) {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.INVALID_INPUT,
      "base45ToBytes: caracter no valido",
      { index: idx, char: text[idx] }
    );
  }
  return v;
}

// src/encoding/base58.ts
var B58_CHARS = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
var B58_LOOKUP = (() => {
  const arr = new Int16Array(128);
  arr.fill(-1);
  for (let i = 0; i < B58_CHARS.length; i++) arr[B58_CHARS.charCodeAt(i)] = i;
  return arr;
})();
function bytesToBase58(bytes) {
  if (bytes.length === 0) return "";
  let zeros = 0;
  while (zeros < bytes.length && bytes[zeros] === 0) zeros++;
  const size = Math.ceil((bytes.length - zeros) * 138 / 100) + 1;
  const b58 = new Uint8Array(size);
  let length = 0;
  for (let i = zeros; i < bytes.length; i++) {
    let carry = bytes[i];
    let j = 0;
    for (let k = size - 1; (carry !== 0 || j < length) && k >= 0; k--, j++) {
      carry += 256 * b58[k];
      b58[k] = carry % 58;
      carry = carry / 58 | 0;
    }
    length = j;
  }
  let it = size - length;
  while (it < size && b58[it] === 0) it++;
  let out = "";
  for (let i = 0; i < zeros; i++) out += "1";
  for (; it < size; it++) out += B58_CHARS[b58[it]];
  return out;
}
function base58ToBytes(text) {
  if (typeof text !== "string") {
    throw new XoneCryptoError(
      XONE_CRYPTO_ERROR_CODES.INVALID_INPUT,
      "base58ToBytes: la entrada debe ser string"
    );
  }
  if (text.length === 0) return new Uint8Array(0);
  let zeros = 0;
  while (zeros < text.length && text[zeros] === "1") zeros++;
  const size = Math.ceil(text.length * 733 / 1e3) + 1;
  const b256 = new Uint8Array(size);
  let length = 0;
  for (let i = zeros; i < text.length; i++) {
    const ch = text.charCodeAt(i);
    const v = ch < 128 ? B58_LOOKUP[ch] ?? -1 : -1;
    if (v < 0) {
      throw new XoneCryptoError(
        XONE_CRYPTO_ERROR_CODES.INVALID_INPUT,
        "base58ToBytes: caracter no valido",
        { index: i, char: text[i] }
      );
    }
    let carry = v;
    let j = 0;
    for (let k = size - 1; (carry !== 0 || j < length) && k >= 0; k--, j++) {
      carry += 58 * b256[k];
      b256[k] = carry & 255;
      carry >>= 8;
    }
    length = j;
  }
  let it = size - length;
  while (it < size && b256[it] === 0) it++;
  const out = new Uint8Array(zeros + (size - it));
  for (let i = 0; i < zeros; i++) out[i] = 0;
  let oi = zeros;
  while (it < size) out[oi++] = b256[it++];
  return out;
}

// src/wrappers/ScriptKey.ts
var ScriptKey = class {
  _alias;
  _algorithm;
  _format;
  _encoded;
  _destroyed = false;
  constructor(init) {
    this._alias = init.alias ?? "";
    this._algorithm = init.algorithm;
    this._format = init.format;
    this._encoded = init.encoded;
  }
  getAlias() {
    return this._alias;
  }
  getAlgorithm() {
    this.assertAlive();
    return this._algorithm;
  }
  getFormat() {
    this.assertAlive();
    return this._format;
  }
  getEncoded() {
    this.assertAlive();
    return this._encoded.slice();
  }
  toString() {
    const status = this._destroyed ? " destroyed" : "";
    return `ScriptKey[${this._algorithm}/${this._format}${status}]`;
  }
  assertAlive() {
    if (this._destroyed) {
      throw new XoneCryptoError(
        XONE_CRYPTO_ERROR_CODES.KEY_DESTROYED,
        "La clave ha sido destruida y no puede usarse"
      );
    }
  }
};
var ScriptPrivateKey = class _ScriptPrivateKey extends ScriptKey {
  isDestroyed() {
    return this._destroyed;
  }
  destroy() {
    if (this._destroyed) return;
    for (let i = 0; i < this._encoded.length; i++) {
      this._encoded[i] = 0;
    }
    this._encoded = new Uint8Array(0);
    this._destroyed = true;
  }
  toPem() {
    this.assertAlive();
    const label = this._format === "PKCS#8" ? "PRIVATE KEY" : "RSA PRIVATE KEY";
    return bytesToPem(label, this._encoded);
  }
  /** ASINCRONO: escribe el PEM en OPFS via el `IFileWriter` inyectado. */
  async toPemFile(virtualPath, writer) {
    const resolved = writer ?? FileWriterRegistry.current;
    if (!resolved) {
      throw new XoneCryptoError(
        XONE_CRYPTO_ERROR_CODES.NO_FILE_WRITER,
        "toPemFile: no hay IFileWriter inyectado (pasar via createCrypto)"
      );
    }
    await resolved.writeText(virtualPath, this.toPem());
  }
  /** Parser PEM. Sincrono — el cuerpo es DER opaco hasta que se importe a Subtle. */
  static fromPem(pem, alias) {
    const block = parsePem(pem);
    let format;
    let algorithm;
    if (block.label === "PRIVATE KEY") {
      format = "PKCS#8";
      algorithm = "UNKNOWN";
    } else if (block.label === "RSA PRIVATE KEY") {
      format = "PKCS#1";
      algorithm = "RSA";
    } else if (block.label === "EC PRIVATE KEY") {
      format = "SEC1";
      algorithm = "EC";
    } else {
      throw new XoneCryptoError(
        XONE_CRYPTO_ERROR_CODES.INVALID_PEM,
        `ScriptPrivateKey.fromPem: etiqueta no soportada: ${block.label}`,
        { label: block.label }
      );
    }
    return new _ScriptPrivateKey({
      ...alias !== void 0 ? { alias } : {},
      algorithm,
      format,
      encoded: block.body
    });
  }
};
var ScriptPublicKey = class _ScriptPublicKey extends ScriptKey {
  toPem() {
    this.assertAlive();
    const label = this._format === "X.509" || this._format === "SPKI" ? "PUBLIC KEY" : "RSA PUBLIC KEY";
    return bytesToPem(label, this._encoded);
  }
  /** ASINCRONO. */
  async toPemFile(virtualPath, writer) {
    const resolved = writer ?? FileWriterRegistry.current;
    if (!resolved) {
      throw new XoneCryptoError(
        XONE_CRYPTO_ERROR_CODES.NO_FILE_WRITER,
        "toPemFile: no hay IFileWriter inyectado (pasar via createCrypto)"
      );
    }
    await resolved.writeText(virtualPath, this.toPem());
  }
  static fromPem(pem, alias) {
    const block = parsePem(pem);
    let format;
    let algorithm;
    if (block.label === "PUBLIC KEY") {
      format = "X.509";
      algorithm = "UNKNOWN";
    } else if (block.label === "RSA PUBLIC KEY") {
      format = "PKCS#1";
      algorithm = "RSA";
    } else {
      throw new XoneCryptoError(
        XONE_CRYPTO_ERROR_CODES.INVALID_PEM,
        `ScriptPublicKey.fromPem: etiqueta no soportada: ${block.label}`,
        { label: block.label }
      );
    }
    return new _ScriptPublicKey({
      ...alias !== void 0 ? { alias } : {},
      algorithm,
      format,
      encoded: block.body
    });
  }
};
var FileWriterRegistry = {
  current: void 0
};

// src/factory/createCrypto.ts
function createCrypto(opts = {}) {
  if (opts.fileWriter) {
    FileWriterRegistry.current = opts.fileWriter;
  }
  return {
    // hash
    hash,
    md5,
    sha1: (input, encoding) => hash("sha1", input, encoding),
    sha256: (input, encoding) => hash("sha256", input, encoding),
    sha384: (input, encoding) => hash("sha384", input, encoding),
    sha512: (input, encoding) => hash("sha512", input, encoding),
    // HMAC / AES / RSA
    hmac,
    encrypt,
    decrypt,
    verify,
    // codificacion
    toBase32: bytesToBase32,
    fromBase32: base32ToBytes,
    toBase45: bytesToBase45,
    fromBase45: base45ToBytes,
    toBase58: bytesToBase58,
    fromBase58: base58ToBytes,
    toBase64: bytesToBase64,
    fromBase64: base64ToBytes,
    // compresion
    deflate,
    inflate,
    // aleatoriedad
    randomBytes,
    getRandomInt,
    getRandomDouble,
    getRandomString,
    getNewUuid,
    // identidad
    toString: () => "ScriptCrypto"
  };
}

// src/wrappers/ScriptCertificate.ts
var ScriptCertificate = class _ScriptCertificate {
  _der;
  _subject;
  _issuer;
  _sigAlgo;
  _notBefore;
  _notAfter;
  _serial;
  constructor(init) {
    if (!(init.der instanceof Uint8Array) || init.der.length === 0) {
      throw new XoneCryptoError(
        XONE_CRYPTO_ERROR_CODES.INVALID_INPUT,
        "ScriptCertificate: se requiere DER no vacio"
      );
    }
    this._der = init.der;
    this._subject = init.subjectPrincipal ?? "UNKNOWN";
    this._issuer = init.issuerPrincipal ?? "UNKNOWN";
    this._sigAlgo = init.signatureAlgorithm ?? "UNKNOWN";
    this._notBefore = init.notBefore ?? /* @__PURE__ */ new Date(0);
    this._notAfter = init.notAfter ?? /* @__PURE__ */ new Date(864e13);
    this._serial = init.serialNumber ?? "";
  }
  /**
   * Construye un `ScriptCertificate` desde PEM. SINCRONO — el parser local
   * solo extrae bloques PEM; el cuerpo DER se conserva opaco.
   *
   * Para parsing completo X.509 (subject, fechas) se inyectara
   * `@peculiar/x509` en una iteracion posterior.
   */
  static fromPem(pem) {
    const block = parsePem(pem);
    if (block.label !== "CERTIFICATE") {
      throw new XoneCryptoError(
        XONE_CRYPTO_ERROR_CODES.INVALID_PEM,
        `ScriptCertificate.fromPem: etiqueta esperada CERTIFICATE, recibida ${block.label}`,
        { label: block.label }
      );
    }
    return new _ScriptCertificate({ der: block.body });
  }
  checkValidity(date = /* @__PURE__ */ new Date()) {
    if (date < this._notBefore) {
      throw new XoneCryptoError(
        XONE_CRYPTO_ERROR_CODES.CERT_NOT_VALID,
        "ScriptCertificate.checkValidity: certificado aun no valido",
        { now: date.toISOString(), notBefore: this._notBefore.toISOString() }
      );
    }
    if (date > this._notAfter) {
      throw new XoneCryptoError(
        XONE_CRYPTO_ERROR_CODES.CERT_NOT_VALID,
        "ScriptCertificate.checkValidity: certificado caducado",
        { now: date.toISOString(), notAfter: this._notAfter.toISOString() }
      );
    }
  }
  getSignatureAlgorithm() {
    return this._sigAlgo;
  }
  getSubjectPrincipal() {
    return this._subject;
  }
  getIssuerPrincipal() {
    return this._issuer;
  }
  getNotBeforeDate() {
    return new Date(this._notBefore.getTime());
  }
  getNotAfterDate() {
    return new Date(this._notAfter.getTime());
  }
  getSerialNumber() {
    return this._serial;
  }
  getEncoded() {
    return this._der.slice();
  }
  toPem() {
    return bytesToPem("CERTIFICATE", this._der);
  }
  /** ASINCRONO. Escribe el PEM al `IFileWriter` inyectado. */
  async toPemFile(virtualPath, writer) {
    const resolved = writer ?? FileWriterRegistry.current;
    if (!resolved) {
      throw new XoneCryptoError(
        XONE_CRYPTO_ERROR_CODES.NO_FILE_WRITER,
        "toPemFile: no hay IFileWriter inyectado (pasar via createCrypto)"
      );
    }
    await resolved.writeText(virtualPath, this.toPem());
  }
  toString() {
    return `ScriptCertificate[subject=${this._subject} algo=${this._sigAlgo}]`;
  }
};

// src/wrappers/ScriptKeyPairCertificate.ts
var ScriptKeyPairCertificate = class {
  _publicKey;
  _privateKey;
  _chain;
  _alias;
  constructor(init) {
    this._publicKey = init.publicKey;
    this._privateKey = init.privateKey;
    this._chain = init.certificateChain ?? [];
    this._alias = init.alias ?? "";
  }
  getAlias() {
    return this._alias;
  }
  getPublicKey() {
    return this._publicKey;
  }
  getPrivateKey() {
    return this._privateKey;
  }
  getCertificateChain() {
    return this._chain;
  }
  toString() {
    return `ScriptKeyPairCertificate[alias=${this._alias} chainLen=${this._chain.length}]`;
  }
};

// src/wrappers/Bundle.ts
var Bundle = class {
  _data = /* @__PURE__ */ new Map();
  putArrayList(key, list) {
    this._data.set(key, list);
    return this;
  }
  putString(key, value) {
    this._data.set(key, value);
    return this;
  }
  putInt(key, value) {
    this._data.set(key, value | 0);
    return this;
  }
  putBoolean(key, value) {
    this._data.set(key, !!value);
    return this;
  }
  getString(key) {
    const v = this._data.get(key);
    return typeof v === "string" ? v : null;
  }
  getInt(key, defaultValue = 0) {
    const v = this._data.get(key);
    return typeof v === "number" ? v : defaultValue;
  }
  getBoolean(key, defaultValue = false) {
    const v = this._data.get(key);
    return typeof v === "boolean" ? v : defaultValue;
  }
  getArrayList(key) {
    const v = this._data.get(key);
    return Array.isArray(v) ? v : null;
  }
  containsKey(key) {
    return this._data.has(key);
  }
  keys() {
    return [...this._data.keys()];
  }
  toString() {
    const entries = [];
    for (const [k, v] of this._data) {
      entries.push(`${k}=${JSON.stringify(v)}`);
    }
    return `Bundle{${entries.join(", ")}}`;
  }
};
export {
  Bundle,
  FileWriterRegistry,
  ScriptCertificate,
  ScriptKey,
  ScriptKeyPairCertificate,
  ScriptPrivateKey,
  ScriptPublicKey,
  XONE_CRYPTO_ERROR_CODES,
  XoneCryptoError,
  base32ToBytes,
  base45ToBytes,
  base58ToBytes,
  base64ToBytes,
  bytesToBase32,
  bytesToBase45,
  bytesToBase58,
  bytesToBase64,
  bytesToHex,
  bytesToPem,
  createCrypto,
  decrypt,
  deflate,
  encrypt,
  getNewUuid,
  getRandomDouble,
  getRandomInt,
  getRandomString,
  hash,
  hexToBytes,
  hmac,
  inflate,
  isSupportedHashAlgorithm,
  md5,
  parsePem,
  randomBytes,
  verify
};
//# sourceMappingURL=index.js.map